import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePirate, formatBerry, LEVELS } from "../lib/PirateContext";
import { X, TrendingUp, Coins, Sparkles, ChevronRight } from "lucide-react";
import { openCrewHierarchy } from "./CrewHierarchySheet";

export default function PirateHUD() {
    const { xp, berry, level, nextLevel, progress } = usePirate();
    const [open, setOpen] = useState(false);

    return (
        <>
            {/* Floating badge (collapsed) */}
            <motion.button
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 1, type: "spring", stiffness: 220 }}
                onClick={() => setOpen(true)}
                className="fixed left-4 bottom-4 z-40 md:left-auto md:right-4 md:bottom-24"
                data-testid="pirate-hud-toggle"
                aria-label="Log del pirata"
            >
                <div className="relative bounty-card p-3 pr-4 flex items-center gap-3 bg-parchment hover:shadow-manga-lg transition-manga">
                    <div className="w-10 h-10 bg-bounty text-parchment border-2 border-ink flex items-center justify-center font-display text-xl">
                        {level.level}
                    </div>
                    <div className="text-left leading-tight">
                        <div className="font-hand text-xs text-ink/70 -mb-1">
                            livello
                        </div>
                        <div className="font-display text-base tracking-tight">
                            {level.name.toUpperCase()}
                        </div>
                    </div>
                    <div className="hidden sm:block h-8 w-px bg-ink/20 mx-1" />
                    <div className="hidden sm:block text-left leading-tight">
                        <div className="font-hand text-xs text-ink/70 -mb-1">
                            tesoro
                        </div>
                        <div
                            className="font-mono text-sm text-bounty font-bold"
                            data-testid="pirate-berry-mini"
                        >
                            {formatBerry(berry)}
                        </div>
                    </div>
                </div>

                {/* Progress ring */}
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gold border-2 border-ink rounded-full animate-pulse" />
            </motion.button>

            {/* Detailed panel */}
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                            className="fixed inset-0 bg-ink/60 z-40"
                        />
                        <motion.aside
                            initial={{ y: "100%", opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: "100%", opacity: 0 }}
                            transition={{ type: "spring", damping: 22 }}
                            className="fixed bottom-0 left-0 right-0 md:bottom-4 md:right-4 md:left-auto md:w-[440px] z-50 bg-parchment border-2 border-ink shadow-manga-lg"
                            data-testid="pirate-hud-panel"
                        >
                            <div className="flex items-center justify-between p-5 bg-ink text-parchment border-b-2 border-ink">
                                <div className="flex items-center gap-3">
                                    <div className="w-11 h-11 bg-bounty border-2 border-parchment flex items-center justify-center font-display text-2xl">
                                        {level.level}
                                    </div>
                                    <div>
                                        <div className="font-hand text-sm text-gold -mb-1">
                                            il tuo diario di bordo
                                        </div>
                                        <div className="font-display text-2xl tracking-tight">
                                            {level.name.toUpperCase()}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setOpen(false)}
                                    aria-label="Chiudi"
                                    className="w-9 h-9 border-2 border-parchment flex items-center justify-center hover:bg-bounty"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="p-5 space-y-5">
                                {/* XP Progress */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-mono text-xs tracking-widest uppercase flex items-center gap-1">
                                            <TrendingUp size={14} /> Esperienza
                                        </div>
                                        <div className="font-mono text-xs text-ink/60">
                                            {xp} XP
                                            {nextLevel && (
                                                <>
                                                    {" / "}
                                                    {nextLevel.threshold}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="h-6 border-2 border-ink bg-white relative overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${progress}%` }}
                                            transition={{
                                                duration: 0.8,
                                                ease: "easeOut",
                                            }}
                                            className="h-full bg-bounty relative"
                                            data-testid="pirate-xp-bar"
                                        >
                                            <div className="absolute inset-0 halftone-red opacity-40" />
                                        </motion.div>
                                    </div>
                                    {nextLevel && (
                                        <div className="font-hand text-sm text-ink/70 mt-2">
                                            ancora {nextLevel.threshold - xp} XP
                                            per diventare{" "}
                                            <span className="text-bounty">
                                                {nextLevel.name}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Berry balance */}
                                <div className="bounty-card p-4 bg-ink text-parchment relative overflow-hidden">
                                    <div className="absolute inset-0 halftone opacity-10" />
                                    <div className="relative flex items-center justify-between">
                                        <div>
                                            <div className="font-hand text-lg text-gold flex items-center gap-1">
                                                <Coins size={14} /> tesoro
                                                accumulato
                                            </div>
                                            <div
                                                className="font-display text-4xl text-parchment tracking-tight"
                                                data-testid="pirate-berry-total"
                                            >
                                                {formatBerry(berry)}
                                            </div>
                                        </div>
                                        <Sparkles
                                            size={40}
                                            className="text-gold opacity-70"
                                        />
                                    </div>
                                    <div className="relative mt-3 pt-3 border-t border-parchment/20 text-xs font-mono text-parchment/70">
                                        Ogni 1000 Berry = 1€ di sconto al
                                        checkout
                                    </div>
                                </div>

                                {/* Level ladder — trigger for bottom sheet */}
                                <div>
                                    <div className="font-mono text-xs tracking-widest uppercase mb-3">
                                        gerarchia della ciurma
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOpen(false);
                                            openCrewHierarchy();
                                        }}
                                        data-testid="pirate-hud-open-hierarchy"
                                        className="w-full flex items-center gap-3 px-3 py-2.5 border-2 border-ink bg-white hover:bg-seafoam transition-manga"
                                    >
                                        <span className="font-display w-6 text-center">
                                            {level.level}
                                        </span>
                                        <span className="text-lg">
                                            {level.badge}
                                        </span>
                                        <span className="font-display tracking-wider flex-1 text-left">
                                            {level.name}
                                        </span>
                                        <span className="font-hand text-sm text-ink/70">
                                            vedi tutti i {LEVELS.length} ranghi
                                        </span>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>

                                <p className="text-xs font-mono text-ink/60 leading-relaxed">
                                    Guadagna XP e Berry esplorando figure,
                                    aggiungendole al bottino, iscrivendoti alla
                                    ciurma o completando un ordine.
                                </p>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
